import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { campaignAPI } from '../services/api';
import { useRules } from '../hooks/useRules';
import CampaignAccessMenu, { LockedBadge } from '../components/CampaignAccessMenu';
import { USE_DEMO_DATA } from '../data/demoMode';
import { DUMMY_POSITIONS, computeDummyStatusCounts } from '../data/campaignsDummyData';
import './Campaigns.css';

const USE_CAMPAIGNS_DUMMY_DATA = USE_DEMO_DATA;

const PAGE_SIZE = 12;

const STATUS_LABEL = {
  draft: 'Draft',
  active: 'Active',
  on_hold: 'On Hold',
  fulfilled: 'Fulfilled',
  archived: 'Archived',
};
const STATUS_OPTIONS = ['draft', 'active', 'on_hold', 'fulfilled', 'archived'];

const C = {
  brand: '#6366F1',
  brandHover: '#4F46E5',
  surface: '#FFFFFF',
  surfaceAlt: '#F3F4F6',
  border: '#E5E7EB',
  borderLight: '#F1F5F9',
  t1: '#111827',
  t3: '#6B7280',
  t4: '#9CA3AF',
  t5: '#CBD5E1',
  green: '#10B981',
  amber: '#F59E0B',
  red: '#EF4444',
};

function Pill({ label, type }) {
  const styles = {
    active: { bg: '#ECFDF5', color: '#065F46' },
    inactive: { bg: '#F1F5F9', color: '#64748B' },
    draft: { bg: '#FFFBEB', color: '#B45309' },
    on_hold: { bg: '#F0FDFA', color: '#0F766E' },
    fulfilled: { bg: '#EEF2FF', color: '#4338CA' },
    archived: { bg: '#F1F5F9', color: '#64748B' },
    spanish: { bg: '#EEF2FF', color: '#4338CA' },
    english: { bg: '#F0F9FF', color: '#0369A1' },
    easy: { bg: '#ECFDF5', color: '#059669' },
    medium: { bg: '#FFFBEB', color: '#D97706' },
    hard: { bg: '#FEF2F2', color: '#DC2626' },
  };
  const s = styles[type] || styles.inactive;
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        padding: '3px 8px',
        borderRadius: 6,
        background: s.bg,
        color: s.color,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

function BtnGhostSm({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      style={{
        fontSize: 11,
        background: 'transparent',
        border: `1px solid ${C.border}`,
        color: C.t3,
        padding: '5px 12px',
        borderRadius: 7,
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontWeight: 500,
        transition: 'all .15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#CBD5E1';
        e.currentTarget.style.background = C.surfaceAlt;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = C.border;
        e.currentTarget.style.background = 'transparent';
      }}
    >
      {children}
    </button>
  );
}

/** "Details" quick-view popup for one campaign card — a summary before drilling
 * into the full page. Setup Progress and Candidate Funnel are derived from data
 * Wizetok actually tracks (questions/candidates/sessions/shortlist), not a
 * separately-modeled workflow — there's no formal "screen built" concept here. */
function QuickViewModal({ campaign: c, onClose, onOpenFullView, onNavigateTab, onChangeStatus }) {
  const [savingStatus, setSavingStatus] = useState(false);
  const createdDate = c.created_at
    ? new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const diffNorm = String(c.difficulty_level ?? 'medium').toLowerCase();
  const diffLabel = diffNorm === 'hard' ? 'Hard' : diffNorm === 'medium' ? 'Medium' : diffNorm === 'easy' ? 'Easy' : diffNorm;
  const diffPillType = diffNorm === 'hard' ? 'hard' : diffNorm === 'medium' ? 'medium' : 'easy';

  const totalCandidates = c.total_candidates ?? 0;
  const invited = c.invited_candidates ?? 0;
  const started = c.in_progress_interviews ?? 0;
  const completed = c.completed_interviews ?? 0;
  const shortlisted = c.shortlisted_candidates ?? 0;

  const steps = [
    { label: 'Position defined', done: true },
    { label: 'Questions ready', done: (c.questions_count ?? 0) > 0 },
    { label: 'Candidates invited', done: invited > 0 },
    { label: 'Shortlist ready', done: shortlisted > 0 },
  ];

  const funnel = [
    { label: 'Invited', value: invited, color: C.brand },
    { label: 'Started', value: started, color: C.amber },
    { label: 'Completed', value: completed, color: C.green },
    { label: 'Shortlisted', value: shortlisted, color: '#0D9488' },
  ];

  const actions = [
    { label: 'Questions', subtitle: `${c.questions_count ?? 0} configured`, icon: '📝', bg: '#EEF2FF', onClick: () => onNavigateTab('questions') },
    { label: 'Invite', subtitle: 'Add candidates', icon: '👤', bg: '#ECFDF5', onClick: () => onNavigateTab('candidates') },
    { label: 'Candidates', subtitle: `${totalCandidates} total`, icon: '📋', bg: '#FFFBEB', onClick: () => onNavigateTab('candidates') },
    { label: 'Reports', subtitle: 'View results', icon: '📊', bg: '#F0FDFA', onClick: () => window.open('/admin/reports', '_self') },
  ];

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={onClose}
    >
      <div
        style={{ background: 'white', borderRadius: 14, width: 560, maxWidth: '94vw', maxHeight: '88vh', overflowY: 'auto', padding: 24, boxSizing: 'border-box' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.t1, marginBottom: 6 }}>{c.name ?? c.campaign_name}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                value={c.status || 'active'}
                disabled={savingStatus}
                onChange={async (e) => {
                  const next = e.target.value;
                  setSavingStatus(true);
                  try {
                    await onChangeStatus(c.id, next);
                  } finally {
                    setSavingStatus(false);
                  }
                }}
                style={{
                  fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
                  border: `1px solid ${C.border}`, background: 'white', color: C.t1,
                  cursor: savingStatus ? 'wait' : 'pointer', fontFamily: 'inherit',
                }}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                ))}
              </select>
              <Pill label={diffLabel} type={diffPillType} />
              {createdDate && <span style={{ fontSize: 12, color: C.t4 }}>{createdDate}</span>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 18, color: C.t3, cursor: 'pointer', lineHeight: 1 }}
          >
            ✕
          </button>
        </div>

        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>
            Setup Progress
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            {steps.map((s, i) => (
              <React.Fragment key={s.label}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <div
                    style={{
                      width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: s.done ? C.green : C.surfaceAlt, color: s.done ? 'white' : C.t4, fontSize: 13, fontWeight: 700,
                    }}
                  >
                    {s.done ? '✓' : i + 1}
                  </div>
                  <div style={{ fontSize: 10, color: C.t3, textAlign: 'center', maxWidth: 76 }}>{s.label}</div>
                </div>
                {i < steps.length - 1 && (
                  <div style={{ flex: 1, height: 2, background: s.done ? C.green : C.border, marginTop: 14, minWidth: 12 }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>
            Candidate Funnel · {totalCandidates} total
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {funnel.map((f) => (
              <div key={f.label} style={{ background: C.surfaceAlt, borderRadius: 8, padding: '10px 6px', textAlign: 'center' }}>
                <div style={{ fontSize: 19, fontWeight: 700, color: f.color }}>{f.value}</div>
                <div style={{ fontSize: 10, color: C.t3, marginTop: 3 }}>{f.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>
            Quick Actions
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {actions.map((a) => (
              <button
                key={a.label}
                type="button"
                onClick={a.onClick}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', border: `1px solid ${C.border}`,
                  borderRadius: 8, background: 'white', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                }}
              >
                <div style={{ width: 30, height: 30, borderRadius: 7, background: a.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                  {a.icon}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{a.label}</div>
                  <div style={{ fontSize: 10, color: C.t4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.subtitle}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button
            type="button"
            onClick={onClose}
            style={{ fontSize: 12, color: C.t3, background: 'none', border: `1px solid ${C.border}`, padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={onOpenFullView}
            style={{ fontSize: 12, fontWeight: 600, color: 'white', background: C.brand, border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Open full view →
          </button>
        </div>
      </div>
    </div>
  );
}

const Campaigns = () => {
  const navigate = useNavigate();
  const rules = useRules();
  const [searchParams] = useSearchParams();
  const statusFromUrl = searchParams.get('status');
  const initialFilter =
    statusFromUrl && ['all', 'active', 'draft', 'on_hold', 'fulfilled', 'archived'].includes(statusFromUrl)
      ? statusFromUrl
      : 'all';
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialFilter);
  const [counts, setCounts] = useState({ all: 0, active: 0, draft: 0, on_hold: 0, fulfilled: 0, archived: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [quickViewCampaign, setQuickViewCampaign] = useState(null);

  const applyDummyCounts = useCallback((rows) => {
    setCounts(computeDummyStatusCounts(rows));
  }, []);

  const refreshCounts = useCallback(async () => {
    if (USE_CAMPAIGNS_DUMMY_DATA) return;
    try {
      const response = await campaignAPI.list(undefined);
      const d = response.data || [];
      const byStatus = (s) => d.filter((c) => (c.status || 'active') === s).length;
      setCounts({
        all: d.length,
        active: byStatus('active'),
        draft: byStatus('draft'),
        on_hold: byStatus('on_hold'),
        fulfilled: byStatus('fulfilled'),
        archived: byStatus('archived'),
      });
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!USE_CAMPAIGNS_DUMMY_DATA) refreshCounts();
  }, [refreshCounts]);

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      if (USE_CAMPAIGNS_DUMMY_DATA) {
        const rows = DUMMY_POSITIONS.map((c) => ({ ...c }));
        setCampaigns(rows);
        applyDummyCounts(rows);
        return;
      }
      // Status (draft/active/on_hold/fulfilled/archived) is a client-side filter
      // below — the list endpoint only knows how to filter by the older
      // is_active boolean, so always fetch everything and slice it ourselves.
      const [response, statsRes] = await Promise.all([
        campaignAPI.list(undefined),
        campaignAPI.getBulkStatistics().catch(() => ({ data: {} })),
      ]);
      const stats = statsRes.data || {};
      const rows = (response.data || []).map((c) => {
        const s = stats[c.id] || {};
        return {
          ...c,
          completed_interviews: s.completed_sessions ?? 0,
          in_progress_interviews: s.in_progress_sessions ?? 0,
          total_interviews: s.total_sessions ?? 0,
          avg_score: s.avg_score ?? null,
          total_candidates: s.total_candidates ?? 0,
          invited_candidates: s.invited_candidates ?? 0,
          shortlisted_candidates: s.shortlisted_candidates ?? 0,
        };
      });
      setCampaigns(rows);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, applyDummyCounts]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    setPage(1);
  }, [filter, searchQuery]);

  const filteredCampaigns = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return campaigns.filter((c) => {
      const matchesStatus = filter === 'all' || (c.status || 'active') === filter;
      if (!matchesStatus) return false;
      if (!q) return true;
      const name = (c.name ?? c.campaign_name ?? '').toLowerCase();
      const role = (c.role ?? c.job_role ?? c.position ?? '').toLowerCase();
      return name.includes(q) || role.includes(q);
    });
  }, [campaigns, searchQuery, filter]);

  const totalPages = Math.max(1, Math.ceil(filteredCampaigns.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedCampaigns = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredCampaigns.slice(start, start + PAGE_SIZE);
  }, [filteredCampaigns, page]);

  const paginationStart = filteredCampaigns.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const paginationEnd = Math.min(page * PAGE_SIZE, filteredCampaigns.length);

  const patchLocalCampaign = (campaignId, patch) => {
    setCampaigns((prev) => {
      const next = prev.map((c) => (String(c.id) === String(campaignId) ? { ...c, ...patch } : c));
      applyDummyCounts(next);
      return next;
    });
    setQuickViewCampaign((prev) =>
      prev && String(prev.id) === String(campaignId) ? { ...prev, ...patch } : prev
    );
  };

  const handleDelete = async (campaignId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to mark this position as inactive?')) {
      try {
        if (USE_CAMPAIGNS_DUMMY_DATA) {
          patchLocalCampaign(campaignId, { status: 'archived', is_active: false });
          return;
        }
        await campaignAPI.delete(campaignId);
        await loadCampaigns();
        await refreshCounts();
      } catch (error) {
        alert('Error marking position as inactive: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleReactivate = async (campaignId, e) => {
    e.stopPropagation();
    try {
      if (USE_CAMPAIGNS_DUMMY_DATA) {
        patchLocalCampaign(campaignId, { status: 'active', is_active: true });
        return;
      }
      await campaignAPI.update(campaignId, { is_active: true, status: 'active' });
      await loadCampaigns();
      await refreshCounts();
    } catch (error) {
      alert('Error reactivating position: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="campaigns-page" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading positions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="campaigns-page" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      <main style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '28px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0F172A', marginBottom: 3 }}>All Positions</h2>
          {rules.can('campaign.create') && (
          <button
            type="button"
            onClick={() => navigate('/admin/campaigns/new')}
            style={{
              fontSize: 12,
              background: C.brand,
              color: 'white',
              border: 'none',
              padding: '8px 18px',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600,
              fontFamily: 'inherit',
              boxShadow: '0 2px 8px rgba(99,102,241,.3)',
              transition: 'background .15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = C.brandHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = C.brand;
            }}
          >
            + New Position
          </button>
          )}
        </div>
        <div style={{ fontSize: 12, color: '#475569', marginBottom: 20 }}>
          Manage hiring positions and invite candidates.
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(128px, 1fr))',
            gap: 10,
            marginBottom: 18,
          }}
        >
          {[
            ['all', 'Total', counts.all, C.brand, 'Across all statuses'],
            ['active', 'Active', counts.active, C.green, 'Live positions'],
            ['draft', 'Draft', counts.draft, C.amber, 'Work in progress'],
            ['on_hold', 'On Hold', counts.on_hold, '#0D9488', 'Paused hiring'],
            ['fulfilled', 'Fulfilled', counts.fulfilled, '#6366F1', 'Role filled'],
            ['archived', 'Archived', counts.archived, C.t4, 'Closed positions'],
          ].map(([v, label, count, accent, subtitle]) => {
            const selected = filter === v;
            return (
              <button
                key={v}
                type="button"
                onClick={() => setFilter(v)}
                style={{
                  textAlign: 'left',
                  background: C.surface,
                  border: `1px solid ${selected ? accent : C.border}`,
                  borderRadius: 10,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  padding: 0,
                  boxShadow: selected ? `0 0 0 3px ${accent}22` : 'none',
                  transition: 'all .14s',
                }}
              >
                <div style={{ height: 2, background: accent }} />
                <div style={{ padding: '9px 11px' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.t3, letterSpacing: '.05em', textTransform: 'uppercase' }}>
                    {label}
                  </div>
                  <div style={{ fontSize: 19, fontWeight: 700, color: C.t1, margin: '2px 0' }}>
                    {count}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: C.t3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: accent, flexShrink: 0 }} />
                    {subtitle}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ flex: '1 1 220px', minWidth: 200, maxWidth: 360 }}>
            <input
              id="campaign-search"
              type="search"
              placeholder="Search by name or role…"
              aria-label="Search positions by name or role"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
              style={{
                width: '100%',
                fontSize: 13,
                padding: '8px 12px',
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                fontFamily: 'inherit',
                color: C.t1,
                background: C.surface,
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        {campaigns.length === 0 ? (
          filter === 'inactive' ? (
            <div className="empty-state" style={{ textAlign: 'center', padding: 48 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: '#0F172A', margin: '0 0 8px' }}>
                No inactive positions
              </h3>
              <p style={{ fontSize: 13, color: '#64748B', margin: 0 }}>
                All your positions are currently active.
              </p>
            </div>
          ) : (
            <div className="empty-state">
              <p>No positions found. Create your first position to get started!</p>
              {rules.can('campaign.create') && <button className="btn btn-primary" onClick={() => navigate('/admin/campaigns/new')}>
                Create Position
              </button>}
            </div>
          )
        ) : filteredCampaigns.length === 0 ? (
          <div className="empty-state">
            <p>No positions match your search.</p>
            <button type="button" className="btn btn-primary" onClick={() => setSearchQuery('')}>
              Clear search
            </button>
          </div>
        ) : (
          <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 14,
            }}
          >
            {paginatedCampaigns.map((c) => {
              const avgScore = c.avg_score ?? c.avgScore ?? c.overall_score ?? null;
              const avg = avgScore != null ? Number(avgScore) : null;
              const langCode = (c.language ?? c.lang ?? 'en').toString().toLowerCase();
              const isEs = langCode === 'es' || langCode.startsWith('es');
              const difficultyRaw = c.difficulty_level ?? c.difficulty ?? 'medium';
              const diffNorm = String(difficultyRaw).toLowerCase();
              const diffLabel =
                diffNorm === 'hard'
                  ? 'Hard'
                  : diffNorm === 'medium'
                    ? 'Medium'
                    : diffNorm === 'easy'
                      ? 'Easy'
                      : String(difficultyRaw).charAt(0).toUpperCase() + String(difficultyRaw).slice(1);
              const diffPillType = diffNorm === 'hard' ? 'hard' : diffNorm === 'medium' ? 'medium' : 'easy';
              const isActive = !!(c.is_active ?? c.status === 'active');
              const interviews =
                c.completed_interviews ?? c.total_interviews ?? 0;
              const candidates =
                c.total_candidates ?? c.candidates ?? 0;
              const scoreColor = avg != null ? (avg >= 8 ? C.green : avg >= 6 ? C.amber : C.red) : C.t5;

              const sourceTool = c.source_tool || null;
              const sourceLabel = sourceTool === 'reachout' ? 'ReachOut'
                : sourceTool === 'codesense' ? 'Codesense'
                : sourceTool === 'wizetok' ? 'Wizetok'
                : null;
              const sourceBg = sourceTool === 'reachout' ? '#FFF7ED'
                : sourceTool === 'codesense' ? '#F0FDF4' : null;
              const sourceColor = sourceTool === 'reachout' ? '#C2410C'
                : sourceTool === 'codesense' ? '#166534' : null;

              const createdDate = c.created_at
                ? new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : null;

              return (
                <div
                  key={c.id}
                  onClick={() => navigate(`/admin/campaigns/${c.id}`)}
                  style={{
                    background: C.surface,
                    borderRadius: 10,
                    border: `1px solid ${C.border}`,
                    overflow: 'hidden',
                    transition: 'all .18s',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = `${C.brand}80`;
                    e.currentTarget.style.boxShadow = `0 4px 16px ${C.brand}1a`;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = C.border;
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'none';
                  }}
                >
                  <div style={{ height: 2, background: isActive ? C.brand : C.t5 }} />

                  <div style={{ padding: '16px' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: 4,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: C.t1,
                          lineHeight: 1.3,
                          flex: 1,
                          marginRight: 8,
                        }}
                      >
                        {c.name ?? c.campaign_name}
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        {sourceLabel && (
                          <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 5, background: sourceBg, color: sourceColor, whiteSpace: 'nowrap' }}>
                            {sourceLabel}
                          </span>
                        )}
                        <Pill
                          label={STATUS_LABEL[c.status] || 'Active'}
                          type={c.status || 'active'}
                        />
                      </div>
                    </div>

                    <div style={{ fontSize: 12, color: C.t4, marginBottom: c.client_name || c.job_code ? 4 : 12 }}>
                      {c.role ?? c.job_role ?? c.position ?? '—'}
                    </div>
                    {(c.client_name || c.job_code) && (
                      <div style={{ display: 'flex', gap: 8, fontSize: 11, color: C.t3, marginBottom: 12 }}>
                        {c.client_name && <span>{c.client_name}</span>}
                        {c.client_name && c.job_code && <span style={{ color: C.t5 }}>·</span>}
                        {c.job_code && <span style={{ fontFamily: 'monospace', color: C.t4 }}>{c.job_code}</span>}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                      <Pill
                        label={isEs ? 'Spanish' : 'English'}
                        type={isEs ? 'spanish' : 'english'}
                      />
                      <Pill label={diffLabel} type={diffPillType} />
                    </div>

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 8,
                        marginBottom: 14,
                      }}
                    >
                      {[
                        [interviews, 'Interviews', C.brand],
                        [avg != null ? avg.toFixed(1) : '—', 'Avg Score', scoreColor],
                      ].map(([val, label, color]) => (
                        <div
                          key={label}
                          style={{
                            background: C.surfaceAlt,
                            borderRadius: 7,
                            padding: '10px 12px',
                            textAlign: 'center',
                            border: `1px solid ${C.borderLight}`,
                          }}
                        >
                          <div style={{ fontSize: 18, fontWeight: 700, color, lineHeight: 1 }}>{val}</div>
                          <div
                            style={{
                              fontSize: 9,
                              color: C.t4,
                              textTransform: 'uppercase',
                              letterSpacing: '.6px',
                              marginTop: 4,
                              fontWeight: 500,
                            }}
                          >
                            {label}
                          </div>
                        </div>
                      ))}
                    </div>

                    {(createdDate || sourceLabel) && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        {createdDate && (
                          <span style={{ fontSize: 11, color: C.t4 }}>
                            {sourceLabel ? 'Synced' : 'Created'}: {createdDate}
                          </span>
                        )}
                        {sourceLabel && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: sourceBg || C.surfaceAlt, color: sourceColor || C.t3 }}>
                            via {sourceLabel}
                          </span>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <LockedBadge campaign={c} />
                      {isActive ? (
                        <>
                          {rules.canOn(c, 'campaign.edit') && (
                            <BtnGhostSm onClick={() => navigate(`/admin/campaigns/${c.id}/edit`)}>
                              Edit
                            </BtnGhostSm>
                          )}
                          {rules.canOn(c, 'campaign.status') && (
                            <BtnGhostSm onClick={(e) => handleDelete(c.id, e)}>
                              Mark inactive
                            </BtnGhostSm>
                          )}
                        </>
                      ) : (
                        rules.canOn(c, 'campaign.status') && (
                          <BtnGhostSm onClick={(e) => handleReactivate(c.id, e)}>
                            Reactivate
                          </BtnGhostSm>
                        )
                      )}
                      <CampaignAccessMenu campaign={c} onChanged={loadCampaigns} />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setQuickViewCampaign(c);
                        }}
                        style={{
                          marginLeft: 'auto',
                          fontSize: 12,
                          color: C.brand,
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: 500,
                          fontFamily: 'inherit',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = 'none';
                        }}
                      >
                        Details →
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 24,
              padding: '12px 18px',
              background: C.surface,
              border: `1px solid ${C.borderLight}`,
              borderRadius: 10,
              flexWrap: 'wrap',
              gap: 12,
            }}
          >
            <div style={{ fontSize: 12, color: '#64748B', fontVariantNumeric: 'tabular-nums' }}>
              Showing {paginationStart}-{paginationEnd} of {filteredCampaigns.length}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={{
                  fontSize: 11,
                  padding: '5px 12px',
                  borderRadius: 7,
                  border: `1px solid ${C.border}`,
                  background: page <= 1 ? '#F1F5F9' : C.surface,
                  color: page <= 1 ? C.t4 : C.t1,
                  cursor: page <= 1 ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  fontWeight: 500,
                }}
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, idx) => idx + 1)
                .slice(Math.max(0, page - 3), Math.min(totalPages, page + 2))
                .map((pn) => (
                  <button
                    key={pn}
                    type="button"
                    onClick={() => setPage(pn)}
                    style={{
                      fontSize: 11,
                      minWidth: 32,
                      height: 32,
                      padding: '0 8px',
                      borderRadius: 7,
                      border: `1px solid ${pn === page ? '#C7D2FE' : C.border}`,
                      background: pn === page ? '#EEF2FF' : C.surface,
                      color: pn === page ? '#4338CA' : C.t1,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontWeight: pn === page ? 700 : 500,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {pn}
                  </button>
                ))}
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                style={{
                  fontSize: 11,
                  padding: '5px 12px',
                  borderRadius: 7,
                  border: `1px solid ${C.border}`,
                  background: page >= totalPages ? '#F1F5F9' : C.surface,
                  color: page >= totalPages ? C.t4 : C.t1,
                  cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  fontWeight: 500,
                }}
              >
                Next
              </button>
            </div>
          </div>
          </>
        )}
      </main>
      {quickViewCampaign && (
        <QuickViewModal
          campaign={quickViewCampaign}
          onClose={() => setQuickViewCampaign(null)}
          onOpenFullView={() => {
            navigate(`/admin/campaigns/${quickViewCampaign.id}`);
            setQuickViewCampaign(null);
          }}
          onNavigateTab={(tab) => {
            navigate(`/admin/campaigns/${quickViewCampaign.id}?tab=${tab}`);
            setQuickViewCampaign(null);
          }}
          onChangeStatus={async (campaignId, nextStatus) => {
            try {
              const patch = { status: nextStatus, is_active: nextStatus !== 'archived' };
              if (USE_CAMPAIGNS_DUMMY_DATA) {
                patchLocalCampaign(campaignId, patch);
                return;
              }
              await campaignAPI.update(campaignId, patch);
              setQuickViewCampaign((prev) => (prev ? { ...prev, ...patch } : prev));
              await Promise.all([loadCampaigns(), refreshCounts()]);
            } catch (error) {
              alert('Error updating status: ' + (error.response?.data?.detail || error.message));
            }
          }}
        />
      )}
    </div>
  );
};

export default Campaigns;
